import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItemButton,
  Menu,
  MenuItem,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { type ReactNode, useMemo, useState } from 'react';

import type { CriterionTranscriptionAssignment } from '../../types';
import {
  type AssignmentGroupBy,
  type CounterpartProgress,
  type StatusTab,
  buildGroups,
  byStatus,
  formatCount,
  formatShortDate,
  openAssignmentIds,
  partitionLabellerPills,
  pct,
} from './assignmentProgress';

export interface TranscriptionDescription {
  primary: string;
  secondary: string;
}

export interface AssignmentsBoardSelection {
  groupBy: AssignmentGroupBy;
  groupId: string | null;
}

interface Props {
  assignments: CriterionTranscriptionAssignment[];
  getUserName: (id: string) => string;
  getCriteriaName: (id: string) => string;
  describeTranscription: (transcriptionId: string) => TranscriptionDescription;
  onRequestDelete: (ids: string[], origin?: 'row' | 'bulk') => void;
  onAssignFromSelection: (selectedAssignments: CriterionTranscriptionAssignment[]) => void;
  createPanel: (selection: AssignmentsBoardSelection) => ReactNode;
}

const PAGE = 100;

const PILL_TOOLTIP_SLOT_PROPS = {
  tooltip: {
    sx: {
      maxHeight: 240,
      overflowY: 'auto',
    },
  },
};

const pillTooltip = (items: CounterpartProgress[]): ReactNode => (
  <Box>
    {items.map(item => (
      <Typography key={item.id} variant="caption" display="block">
        {item.label} {item.started}/{item.total}
      </Typography>
    ))}
  </Box>
);

const pillCountLabel = (
  count: number,
  singular: string,
  pluralWord: string,
  suffix: string
): string => `${count} ${count === 1 ? singular : pluralWord} ${suffix}`;

const ProgressPills = ({
  counterparts,
  kind,
}: {
  counterparts: CounterpartProgress[];
  kind: 'labeller' | 'assignment';
}) => {
  const { complete, inProgress } = partitionLabellerPills(counterparts);
  const completeCount =
    kind === 'assignment' ? complete.reduce((sum, item) => sum + item.total, 0) : complete.length;
  const inProgressCount =
    kind === 'assignment'
      ? inProgress.reduce((sum, item) => sum + item.total, 0)
      : inProgress.length;
  const noun = kind === 'assignment' ? 'assignment' : 'labeller';
  const nounPlural = kind === 'assignment' ? 'assignments' : 'labellers';
  const completeSuffix = kind === 'assignment' ? 'complete' : 'finished';

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
      {completeCount > 0 && (
        <Tooltip title={pillTooltip(complete)} slotProps={PILL_TOOLTIP_SLOT_PROPS}>
          <Chip
            size="small"
            color="success"
            label={pillCountLabel(completeCount, noun, nounPlural, completeSuffix)}
            aria-label={pillCountLabel(completeCount, noun, nounPlural, completeSuffix)}
          />
        </Tooltip>
      )}
      {inProgressCount > 0 && (
        <Tooltip title={pillTooltip(inProgress)} slotProps={PILL_TOOLTIP_SLOT_PROPS}>
          <Chip
            size="small"
            color="warning"
            label={pillCountLabel(inProgressCount, noun, nounPlural, 'in progress')}
            aria-label={pillCountLabel(inProgressCount, noun, nounPlural, 'in progress')}
          />
        </Tooltip>
      )}
    </Box>
  );
};

const AssignmentsBoard = ({
  assignments,
  getUserName,
  getCriteriaName,
  describeTranscription,
  onRequestDelete,
  onAssignFromSelection,
  createPanel,
}: Props) => {
  const [groupBy, setGroupBy] = useState<AssignmentGroupBy>('criterion');
  const [search, setSearch] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [status, setStatus] = useState<StatusTab>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [limit, setLimit] = useState(PAGE);
  const [selectMenuAnchor, setSelectMenuAnchor] = useState<HTMLElement | null>(null);

  const groups = useMemo(
    () => buildGroups(assignments, groupBy, getUserName, getCriteriaName),
    [assignments, getCriteriaName, getUserName, groupBy]
  );
  const visibleGroups = useMemo(
    () =>
      search
        ? groups.filter(group => group.label.toLowerCase().includes(search.toLowerCase()))
        : groups,
    [groups, search]
  );
  const activeGroup = groups.find(group => group.id === selectedGroupId) ?? null;
  const scope = activeGroup ? activeGroup.items : assignments;
  const rows = useMemo(() => {
    const filtered = byStatus(scope, status);
    return [...filtered].sort((left, right) => right.assignedDate.localeCompare(left.assignedDate));
  }, [scope, status]);
  const total = scope.length;
  const started = scope.filter(assignment => assignment.status === 'started').length;
  const open = total - started;
  const openIdsInScope = openAssignmentIds(scope);
  const startedIdsInScope = scope
    .filter(assignment => assignment.status === 'started')
    .map(assignment => assignment.id);
  const allIdsInScope = scope.map(assignment => assignment.id);
  const openIdSet = useMemo(() => new Set(openAssignmentIds(assignments)), [assignments]);
  const selectedOpen = Array.from(selected).filter(id => openIdSet.has(id));
  const columnCount = activeGroup === null ? 7 : 6;

  const pickGroup = (id: string | null) => {
    setSelectedGroupId(id);
    setSelected(new Set());
    setLimit(PAGE);
  };
  const changeGroupBy = (next: AssignmentGroupBy | null) => {
    if (!next) return;
    setGroupBy(next);
    pickGroup(null);
  };
  const toggle = (id: string) =>
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const selectByStatus = (ids: string[]) => {
    setSelected(new Set(ids));
    setSelectMenuAnchor(null);
  };

  const handleAssignFromSelection = () => {
    const selectedAssignments = assignments.filter(assignment => selected.has(assignment.id));
    if (selectedAssignments.length === 0) return;
    onAssignFromSelection(selectedAssignments);
  };

  const counterpartHeader = groupBy === 'criterion' ? 'Labeller' : 'Criterion';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        height: 'calc(100vh - 16px)',
        position: 'relative',
        pb: selected.size > 0 ? 8 : 0,
      }}
    >
      <Box
        sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}
      >
        <Box>
          <Typography variant="h4" component="h1">
            Assignments
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>{formatCount(assignments.length)}</strong> assignments ·{' '}
            <strong>{formatCount(assignments.filter(a => a.status === 'started').length)}</strong>{' '}
            started ·{' '}
            <strong>{formatCount(assignments.filter(a => a.status === 'open').length)}</strong> open
            · <strong>{new Set(assignments.map(a => a.transcriptionId)).size}</strong> transcripts
          </Typography>
        </Box>
        {createPanel({ groupBy, groupId: selectedGroupId })}
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0 }}>
        <Paper
          variant="outlined"
          sx={{ width: 320, flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}
        >
          <Box sx={{ p: 1.5, display: 'grid', gap: 1 }}>
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={groupBy}
              onChange={(_event, next: AssignmentGroupBy | null) => changeGroupBy(next)}
              aria-label="Assignment grouping"
            >
              <ToggleButton value="criterion">Criteria</ToggleButton>
              <ToggleButton value="labeller">Labellers</ToggleButton>
            </ToggleButtonGroup>
            <TextField
              size="small"
              placeholder={`Find ${groupBy === 'criterion' ? 'criterion' : 'labeller'}…`}
              value={search}
              onChange={event => setSearch(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <List dense disablePadding sx={{ overflowY: 'auto', flex: 1 }}>
            <ListItemButton selected={selectedGroupId === null} onClick={() => pickGroup(null)}>
              <Box sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" fontWeight={600}>
                    All {groupBy === 'criterion' ? 'criteria' : 'labellers'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatCount(assignments.length)}
                  </Typography>
                </Box>
              </Box>
            </ListItemButton>
            {visibleGroups.map(group => (
              <ListItemButton
                key={group.id}
                selected={group.id === selectedGroupId}
                onClick={() => pickGroup(group.id)}
                sx={{ alignItems: 'flex-start' }}
              >
                <Box sx={{ width: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                    <Typography
                      variant="caption"
                      title={group.label}
                      sx={{
                        fontSize: groupBy === 'criterion' ? '0.75rem' : '0.8125rem',
                        lineHeight: 1.3,
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                      }}
                    >
                      {group.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ flexShrink: 0, pt: 0.1 }}
                    >
                      {group.started}/{group.total}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={pct(group.started, group.total)}
                    sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
                  />
                </Box>
              </ListItemButton>
            ))}
          </List>
        </Paper>

        <Paper
          variant="outlined"
          sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}
        >
          <Box sx={{ px: 2, pt: 1.5 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
              {activeGroup
                ? activeGroup.label
                : `All ${groupBy === 'criterion' ? 'criteria' : 'labellers'}`}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
              <LinearProgress
                variant="determinate"
                value={pct(started, total)}
                sx={{ flex: 1, maxWidth: 240, height: 6, borderRadius: 3 }}
              />
              <Typography variant="caption" color="text.secondary">
                {pct(started, total)}% started · {formatCount(open)} open ·{' '}
                {activeGroup
                  ? activeGroup.transcripts
                  : new Set(scope.map(assignment => assignment.transcriptionId)).size}{' '}
                transcripts
              </Typography>
            </Box>
            {activeGroup && (
              <ProgressPills
                counterparts={activeGroup.counterparts}
                kind={groupBy === 'criterion' ? 'labeller' : 'assignment'}
              />
            )}
            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}
            >
              <Tabs
                value={status}
                onChange={(_event, next: StatusTab) => {
                  setStatus(next);
                  setLimit(PAGE);
                }}
                aria-label="Assignment status"
                sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0 } }}
              >
                <Tab value="all" label={`All ${formatCount(total)}`} />
                <Tab value="open" label={`Open ${formatCount(open)}`} />
                <Tab value="started" label={`Started ${formatCount(started)}`} />
              </Tabs>
              <Button
                size="small"
                endIcon={<ArrowDropDownIcon />}
                disabled={allIdsInScope.length === 0}
                onClick={event => setSelectMenuAnchor(event.currentTarget)}
                aria-haspopup="menu"
                aria-expanded={Boolean(selectMenuAnchor)}
                aria-controls={selectMenuAnchor ? 'select-assignments-menu' : undefined}
              >
                Select all
              </Button>
              <Menu
                id="select-assignments-menu"
                anchorEl={selectMenuAnchor}
                open={Boolean(selectMenuAnchor)}
                onClose={() => setSelectMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem
                  disabled={openIdsInScope.length === 0}
                  onClick={() => selectByStatus(openIdsInScope)}
                >
                  Open ({formatCount(openIdsInScope.length)})
                </MenuItem>
                <MenuItem
                  disabled={startedIdsInScope.length === 0}
                  onClick={() => selectByStatus(startedIdsInScope)}
                >
                  Started ({formatCount(startedIdsInScope.length)})
                </MenuItem>
                <MenuItem
                  disabled={allIdsInScope.length === 0}
                  onClick={() => selectByStatus(allIdsInScope)}
                >
                  Open and started ({formatCount(allIdsInScope.length)})
                </MenuItem>
              </Menu>
            </Box>
          </Box>

          <Box sx={{ overflow: 'auto', flex: 1 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>{counterpartHeader}</TableCell>
                  {activeGroup === null && (
                    <TableCell>{groupBy === 'criterion' ? 'Criterion' : 'Labeller'}</TableCell>
                  )}
                  <TableCell>Transcription</TableCell>
                  <TableCell>Assigned</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell padding="checkbox" />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.slice(0, limit).map(assignment => {
                  const transcription = describeTranscription(assignment.transcriptionId);
                  const isOpen = assignment.status === 'open';
                  return (
                    <TableRow key={assignment.id} hover selected={selected.has(assignment.id)}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selected.has(assignment.id)}
                          onChange={() => toggle(assignment.id)}
                          inputProps={{ 'aria-label': `select row ${assignment.id}` }}
                        />
                      </TableCell>
                      <TableCell>
                        {groupBy === 'criterion'
                          ? getUserName(assignment.userId)
                          : getCriteriaName(assignment.criteriaId)}
                      </TableCell>
                      {activeGroup === null && (
                        <TableCell>
                          {groupBy === 'criterion'
                            ? getCriteriaName(assignment.criteriaId)
                            : getUserName(assignment.userId)}
                        </TableCell>
                      )}
                      <TableCell>
                        <Typography variant="body2">{transcription.primary}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {transcription.secondary}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2">
                          {formatShortDate(assignment.assignedDate)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          by {getUserName(assignment.assignedBy)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.75,
                            fontSize: 13,
                            color: isOpen ? 'warning.dark' : 'info.dark',
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: isOpen ? 'warning.main' : 'info.main',
                            }}
                          />
                          {isOpen ? 'Open' : 'Started'}
                        </Box>
                      </TableCell>
                      <TableCell padding="checkbox">
                        {isOpen && (
                          <Tooltip title="Delete assignment">
                            <IconButton
                              size="small"
                              aria-label={`Delete assignment ${assignment.id}`}
                              onClick={() => onRequestDelete([assignment.id], 'row')}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={columnCount} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                        No criterion transcription assignments found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            {rows.length > limit && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 1.5 }}>
                <Button size="small" onClick={() => setLimit(current => current + PAGE)}>
                  Show {Math.min(PAGE, rows.length - limit)} more of{' '}
                  {formatCount(rows.length - limit)}
                </Button>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
      {selected.size > 0 && (
        <Paper
          elevation={8}
          role="toolbar"
          aria-label="Selection actions"
          sx={{
            position: 'absolute',
            bottom: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 3,
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'nowrap',
            width: 'max-content',
            whiteSpace: 'nowrap',
            gap: 1.5,
            px: 2,
            py: 1,
            borderRadius: 2,
          }}
        >
          <Typography variant="body2" sx={{ flexShrink: 0 }}>
            <strong>{selected.size}</strong> selected
          </Typography>
          <Button
            size="small"
            variant="contained"
            onClick={handleAssignFromSelection}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            Assign to others
          </Button>
          <Button
            size="small"
            color="error"
            variant="contained"
            startIcon={<DeleteOutlineIcon />}
            disabled={selectedOpen.length === 0}
            onClick={() => onRequestDelete(selectedOpen, 'bulk')}
            aria-label="Delete selected assignments"
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            Delete
          </Button>
          <Button size="small" onClick={() => setSelected(new Set())} sx={{ flexShrink: 0 }}>
            Deselect all
          </Button>
        </Paper>
      )}
    </Box>
  );
};

export default AssignmentsBoard;
